import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCourseContext } from "@/contexts/CourseContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Zap,
  BookOpen,
  Bot,
  Mic,
  Layers,
  MonitorPlay,
  ClipboardCheck,
  CheckCircle2,
  Lock,
  Circle,
  Star,
  Target,
  ChevronRight,
} from "lucide-react";
import { Session } from "@supabase/supabase-js";
import CourseTreeView from "@/components/CourseTreeView";
import CoachSuggestionCard from "@/components/CoachSuggestionCard";
import CoachPanel from "@/components/CoachPanel";
import FurtherReading from "@/components/FurtherReading";

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface CertificationDomain {
  certification: string;
  domain: string;
  objective?: string;
}

interface SkillNode {
  id: string;
  skill_tree_id: string;
  title: string;
  description: string | null;
  level_index: number;
  position_index: number;
  is_key_node: boolean;
  content_summary: string | null;
  chapter_id?: string | null;
  user_status: "locked" | "available" | "in_progress" | "completed";
  certification_domains?: CertificationDomain[];
}

interface SkillChapter {
  id: string;
  title: string;
  description?: string | null;
  order_index: number;
}

interface Prerequisite {
  id: string;
  skill_node_id: string;
  prereq_node_id: string;
}

interface Certification {
  name: string;
  issuer: string;
  url?: string;
  domains?: string[];
}

interface SkillTree {
  id: string;
  course_id: string;
  title: string;
  target_certifications?: Certification[];
}

interface CourseObjective {
  id: string;
  title: string;
  description: string | null;
  order_index: number;
}

// ─── Module-level SWR cache ───────────────────────────────────────────────────
// Lives outside the component so it survives route changes (unmount/remount).
// Key: `${userId}:${courseId}`

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CoursePageCache {
  tree: SkillTree | null;
  nodes: SkillNode[];
  chapters: SkillChapter[];
  prerequisites: Prerequisite[];
  enrolled: boolean;
  designerPreview: boolean;
  objectives: CourseObjective[];
  nodeMastery: Record<string, { mastery_score: number; confidence_tier: string }>;
  objectiveMastery: Record<string, { mastery_score: number }>;
  coachSuggestions: Array<{
    id: string; message: string; suggestion_type: string;
    cta_label: string | null; cta_url: string | null;
    priority?: number; node_id?: string | null; course_id?: string | null;
  }>;
  fetchedAt: number;
}

const coursePageCache = new Map<string, CoursePageCache>();

// ─── Design tokens ───────────────────────────────────────────────────────────
const GREEN_DEEP   = "#1C3829";
const GREEN_ACCENT = "#2F6147";
const CREAM        = "#EAE6DC";
const LIME         = "hsl(65,65%,75%)";

// ─── Sub-components ──────────────────────────────────────────────────────────

function ProgressRing({ pct, size = 72 }: { pct: number; size?: number }) {
  const r    = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (pct / 100);
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke="rgba(234,230,220,0.12)" strokeWidth={6} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={LIME} strokeWidth={6}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round" />
    </svg>
  );
}

interface NodeIdentityBarProps {
  node: SkillNode | undefined;
  onGoToNode: () => void;
  isDesignerPreview: boolean;
}

function NodeIdentityBar({ node, onGoToNode, isDesignerPreview }: NodeIdentityBarProps) {
  if (!node) return null;
  const statusLabel: Record<string, string> = {
    completed: "Completed",
    in_progress: "In Progress",
    available: "Available",
    locked: "Locked",
  };
  const statusColor: Record<string, string> = {
    completed: LIME,
    in_progress: "rgba(212,224,138,0.7)",
    available: "rgba(234,230,220,0.55)",
    locked: "rgba(234,230,220,0.25)",
  };
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 16, padding: "12px 0 4px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {node.is_key_node && (
          <div style={{
            width: 20, height: 20, borderRadius: 10, flexShrink: 0,
            background: LIME, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Star style={{ width: 10, height: 10, color: GREEN_DEEP, fill: GREEN_DEEP }} />
          </div>
        )}
        <span style={{ color: CREAM, fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {node.title}
        </span>
        <span style={{ color: statusColor[node.user_status], fontSize: 12, flexShrink: 0 }}>
          {statusLabel[node.user_status]}
        </span>
      </div>
      {(node.user_status !== "locked" || isDesignerPreview) && (
        <button
          onClick={onGoToNode}
          style={{
            flexShrink: 0, display: "flex", alignItems: "center", gap: 6,
            background: LIME, color: GREEN_DEEP,
            border: "none", borderRadius: 8, padding: "7px 14px",
            fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          Open Node <ArrowRight style={{ width: 14, height: 14 }} />
        </button>
      )}
    </div>
  );
}

interface ModalityTileProps {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
  disabled?: boolean;
  comingSoon?: boolean;
}

function ModalityTile({ icon, label, sublabel, onClick, disabled, comingSoon }: ModalityTileProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 8, padding: "20px 12px", borderRadius: 14, cursor: disabled ? "not-allowed" : "pointer",
        border: `1.5px solid ${CREAM}90`,
        background: "#FFFFFF",
        boxShadow: "0 1px 3px rgba(28,56,41,0.08)",
        transition: "background 0.18s, border-color 0.18s, transform 0.15s",
        position: "relative", opacity: disabled ? 0.55 : 1,
        textAlign: "center",
      }}
      onMouseEnter={e => {
        if (!disabled) {
          (e.currentTarget as HTMLElement).style.background = "#F4F1EA";
          (e.currentTarget as HTMLElement).style.borderColor = "rgba(47,97,71,0.35)";
          (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
        }
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = "#FFFFFF";
        (e.currentTarget as HTMLElement).style.borderColor = `${CREAM}90`;
        (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
      }}
    >
      <div style={{ color: GREEN_ACCENT, opacity: disabled ? 0.6 : 1 }}>{icon}</div>
      <div>
        <p style={{ color: GREEN_DEEP, fontWeight: 700, fontSize: 13, margin: 0 }}>{label}</p>
        <p style={{ color: "#4A6B58", fontSize: 11, margin: 0 }}>{sublabel}</p>
      </div>
      {comingSoon && (
        <span style={{
          position: "absolute", top: 6, right: 6,
          background: "rgba(212,224,138,0.18)", color: LIME,
          fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.04em",
        }}>
          SOON
        </span>
      )}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const LearnCourse = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { setCourseContext } = useCourseContext();
  const isDesignerPreview = searchParams.get("preview") === "designer";

  const [session, setSession]           = useState<Session | null>(null);
  const [enrolled, setEnrolled]         = useState(false);
  const [designerPreview, setDesignerPreview] = useState(false);
  const [enrolling, setEnrolling]       = useState(false);
  const [loading, setLoading]           = useState(true);
  const [tree, setTree]                 = useState<SkillTree | null>(null);
  const [nodes, setNodes]               = useState<SkillNode[]>([]);
  const [chapters, setChapters]         = useState<SkillChapter[]>([]);
  const [prerequisites, setPrerequisites] = useState<Prerequisite[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [objectives, setObjectives]     = useState<CourseObjective[]>([]);
  const [nodeMastery, setNodeMastery]   = useState<Record<string, { mastery_score: number; confidence_tier: string }>>({});
  const [objectiveMastery, setObjectiveMastery] = useState<Record<string, { mastery_score: number }>>({});
  const [coachSuggestions, setCoachSuggestions] = useState<Array<{
    id: string; message: string; suggestion_type: string; cta_label: string | null; cta_url: string | null;
    priority?: number; node_id?: string | null; course_id?: string | null;
  }>>([]);
  const [coachPanelOpen, setCoachPanelOpen] = useState(false);
  const [publicCourse, setPublicCourse] = useState<{ title: string; description: string | null } | null>(null);

  const navigate = useNavigate();
  const { toast } = useToast();
  const modalityRef = useRef<HTMLDivElement>(null);

  // ─── Auth — allow unauthenticated visitors on public courses ───────────────
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (!session) {
        // Check if this course is publicly accessible before redirecting to auth
        const { data: courseData } = await supabase
          .from("learning_projects")
          .select("title, description, is_public")
          .eq("id", id!)
          .maybeSingle();
        if (courseData?.is_public) {
          setPublicCourse({ title: courseData.title, description: courseData.description });
          setLoading(false);
        } else {
          navigate(`/auth?redirect=/learn/courses/${id}`);
        }
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        // Only redirect if not on a public course preview
        if (!publicCourse) navigate(`/auth?redirect=/learn/courses/${id}`);
      } else {
        // User just signed in — clear the public preview and let normal flow take over
        setPublicCourse(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // ─── Unified data loader (SWR: serve cache instantly, refresh in background) ─
  const applyCache = (c: CoursePageCache) => {
    setEnrolled(c.enrolled);
    setTree(c.tree);
    setNodes(c.nodes);
    setChapters(c.chapters);
    setPrerequisites(c.prerequisites);
    setDesignerPreview(c.designerPreview);
    setObjectives(c.objectives);
    setNodeMastery(c.nodeMastery);
    setObjectiveMastery(c.objectiveMastery);
    setCoachSuggestions(c.coachSuggestions);
  };

  const loadCourseData = async (userId: string, courseId: string, background = false) => {
    if (!background) setLoading(true);
    try {
      const [treeResult, objectivesResult, nodesMasteryResult, objMasteryResult, suggestionsResult] =
        await Promise.all([
          supabase.functions.invoke("get-learner-skill-tree", {
            body: { courseId, previewMode: isDesignerPreview },
          }),
          supabase.from("course_objectives")
            .select("id, title, description, order_index")
            .eq("course_id", courseId).order("order_index"),
          supabase.from("learner_node_mastery")
            .select("node_id, mastery_score, confidence_tier")
            .eq("user_id", userId).eq("course_id", courseId),
          supabase.from("learner_objective_mastery")
            .select("objective_id, mastery_score")
            .eq("user_id", userId).eq("course_id", courseId),
          isDesignerPreview ? Promise.resolve({ data: [] }) :
            supabase.from("coach_suggestions")
              .select("id, message, suggestion_type, cta_label, cta_url, priority, node_id, course_id")
              .eq("user_id", userId).eq("course_id", courseId)
              .eq("is_dismissed", false)
              .order("priority", { ascending: false })
              .order("generated_at", { ascending: false })
              .limit(2),
        ]);

      if (treeResult.error) {
        if (!background)
          toast({ variant: "destructive", title: "Error loading course", description: treeResult.error.message });
        return;
      }

      const nm: Record<string, { mastery_score: number; confidence_tier: string }> = {};
      ((nodesMasteryResult as { data: { node_id: string; mastery_score: number; confidence_tier: string }[] | null }).data ?? [])
        .forEach(r => { nm[r.node_id] = { mastery_score: r.mastery_score, confidence_tier: r.confidence_tier }; });

      const om: Record<string, { mastery_score: number }> = {};
      ((objMasteryResult as { data: { objective_id: string; mastery_score: number }[] | null }).data ?? [])
        .forEach(r => { om[r.objective_id] = { mastery_score: r.mastery_score }; });

      const cached: CoursePageCache = {
        tree:             treeResult.data.tree,
        nodes:            treeResult.data.nodes ?? [],
        chapters:         treeResult.data.chapters ?? [],
        prerequisites:    treeResult.data.prerequisites ?? [],
        enrolled:         treeResult.data.enrolled,
        designerPreview:  treeResult.data.designerPreview ?? false,
        objectives:       (objectivesResult as { data: CourseObjective[] | null }).data ?? [],
        nodeMastery:      nm,
        objectiveMastery: om,
        coachSuggestions: (suggestionsResult as { data: CoursePageCache["coachSuggestions"] | null }).data ?? [],
        fetchedAt:        Date.now(),
      };

      coursePageCache.set(`${userId}:${courseId}`, cached);
      applyCache(cached);
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    if (!session?.user?.id || !id) return;
    const key = `${session.user.id}:${id}`;
    const cached = !isDesignerPreview ? coursePageCache.get(key) : undefined;

    if (cached) {
      // Instant render from cache — no loading spinner
      applyCache(cached);
      setLoading(false);
      // Background refresh if stale
      if (Date.now() - cached.fetchedAt > CACHE_TTL) {
        loadCourseData(session.user.id, id, true);
      }
    } else {
      loadCourseData(session.user.id, id, false);
    }
  }, [session?.user?.id, id]);

  // Exposed so handleEnroll can force a fresh fetch and bust the cache
  const fetchLearnerSkillTree = () => {
    if (!session?.user?.id || !id) return;
    coursePageCache.delete(`${session.user.id}:${id}`);
    loadCourseData(session.user.id, id, false);
  };

  // ─── Course context ────────────────────────────────────────────────────────
  useEffect(() => {
    if (id && tree) {
      setCourseContext({ courseId: id, courseName: tree.title, nodeId: null, nodeName: null, pageType: "learner" });
    }
  }, [id, tree, setCourseContext]);

  // ─── Default node selection ────────────────────────────────────────────────
  // Always start at the first node in sequence so the tree pans to the beginning.
  useEffect(() => {
    if (!nodes.length || selectedNodeId) return;
    const sorted = [...nodes].sort(
      (a, b) => a.level_index - b.level_index || a.position_index - b.position_index
    );
    setSelectedNodeId(sorted[0].id);
  }, [nodes]);

  // ─── Derived state ─────────────────────────────────────────────────────────
  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const completedCount = nodes.filter(n => n.user_status === "completed").length;
  const completionPct  = nodes.length ? Math.round((completedCount / nodes.length) * 100) : 0;

  const curriculumGroups = useMemo(() => {
    if (chapters.length > 0) {
      const chapterMap = new Map(chapters.map(c => [c.id, c]));
      const groups: { id?: string; label: string; description?: string | null; nodes: SkillNode[] }[] = [];
      const unassigned: SkillNode[] = [];
      chapters
        .slice()
        .sort((a, b) => a.order_index - b.order_index)
        .forEach(ch => {
          const chNodes = nodes
            .filter(n => n.chapter_id === ch.id)
            .sort((a, b) => a.level_index - b.level_index || a.position_index - b.position_index);
          if (chNodes.length) groups.push({ id: ch.id, label: ch.title, description: ch.description, nodes: chNodes });
        });
      nodes.filter(n => !n.chapter_id || !chapterMap.has(n.chapter_id!)).forEach(n => unassigned.push(n));
      if (unassigned.length) groups.push({ label: "Other", nodes: unassigned.sort((a, b) => a.level_index - b.level_index) });
      return groups;
    }
    const levelMap = new Map<number, SkillNode[]>();
    nodes.forEach(n => { (levelMap.get(n.level_index) ?? levelMap.set(n.level_index, []).get(n.level_index)!).push(n); });
    return [...levelMap.entries()]
      .sort(([a], [b]) => a - b)
      .map(([lvl, lvlNodes]) => ({
        label: `Level ${lvl + 1}`,
        description: null,
        nodes: lvlNodes.sort((a, b) => a.position_index - b.position_index),
      }));
  }, [nodes, chapters]);

  // ─── Navigation helpers ────────────────────────────────────────────────────
  const navigateToNode = (tab?: string) => {
    if (!selectedNodeId || !selectedNode) return;
    let url = `/learn/courses/${id}/nodes/${selectedNodeId}`;
    const params: string[] = [];
    if (isDesignerPreview) params.push("preview=designer");
    if (tab) params.push(`tab=${tab}`);
    if (params.length) url += "?" + params.join("&");
    // Pass node data in state to skip loading-screen fetch in LearnNodeContent
    navigate(url, {
      state: { node: { title: selectedNode.title, is_key_node: selectedNode.is_key_node } },
    });
  };

  const handleModeClick = (mode: string) => {
    if (!selectedNode) return;
    if (selectedNode.user_status === "locked" && !isDesignerPreview) {
      sonnerToast.error("Node is locked", { description: "Complete prerequisites to unlock." });
      return;
    }
    switch (mode) {
      case "read":    navigateToNode();          break;
      case "tutor":   navigateToNode("tutor");   break;
      case "slides":  navigateToNode("slides");  break;
      case "exam":    navigateToNode("exam");    break;
      case "flash":   navigateToNode("flashcards"); break;
      case "voice":   navigateToNode("voice"); break;
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    const { error } = await supabase.functions.invoke("enroll-course", { body: { courseId: id } });
    if (error) {
      toast({ variant: "destructive", title: "Error enrolling", description: error.message });
    } else {
      toast({ title: "Enrolled successfully!", description: "You can now start learning." });
      fetchLearnerSkillTree();
    }
    setEnrolling(false);
  };

  // ─── Public course preview for unauthenticated visitors ───────────────────
  if (!session && publicCourse) {
    return (
      <div style={{ minHeight: "100vh", background: GREEN_DEEP }}>
        <div style={{
          borderBottom: "1px solid rgba(234,230,220,0.08)",
          padding: "16px 24px",
        }}>
          <p style={{ color: LIME, fontWeight: 700, fontSize: 16 }}>Distilled Intuition</p>
        </div>
        <div style={{
          maxWidth: 600, margin: "0 auto", padding: "64px 24px",
          display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 24,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 32,
            background: "rgba(234,230,220,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <BookOpen style={{ width: 28, height: 28, color: LIME }} />
          </div>
          <div style={{ gap: 10, display: "flex", flexDirection: "column" }}>
            <h1 style={{ color: CREAM, fontSize: 28, fontWeight: 800, lineHeight: 1.2, margin: 0 }}>
              {publicCourse.title}
            </h1>
            {publicCourse.description && (
              <p style={{ color: "rgba(234,230,220,0.65)", fontSize: 16, margin: 0 }}>
                {publicCourse.description}
              </p>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 320 }}>
            <button
              onClick={() => navigate(`/auth?redirect=/learn/courses/${id}`)}
              style={{
                background: LIME, color: GREEN_DEEP, border: "none",
                borderRadius: 10, padding: "13px 24px",
                fontWeight: 700, fontSize: 15, cursor: "pointer", width: "100%",
              }}
            >
              Sign up to take this course
            </button>
            <button
              onClick={() => navigate(`/auth?redirect=/learn/courses/${id}&mode=signin`)}
              style={{
                background: "transparent", color: CREAM,
                border: "1px solid rgba(234,230,220,0.25)",
                borderRadius: 10, padding: "13px 24px",
                fontWeight: 600, fontSize: 15, cursor: "pointer", width: "100%",
              }}
            >
              Already have an account? Log in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading / auth guard ──────────────────────────────────────────────────
  if (!session || loading) {
    return (
      <div style={{ minHeight: "100vh", background: GREEN_DEEP, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "rgba(234,230,220,0.55)" }}>Loading…</p>
      </div>
    );
  }

  if (!enrolled && !designerPreview) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b">
          <div className="container mx-auto px-4 py-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </div>
        </header>
        <main className="container mx-auto px-4 py-12">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Start Learning</CardTitle>
              <CardDescription>Enroll in this course to begin your learning journey.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleEnroll} disabled={enrolling} size="lg">
                {enrolling ? "Enrolling…" : "Start Course"}
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // ─── Main layout ───────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))" }}>

      {/* ── Sticky top bar ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 50,
        background: `${GREEN_DEEP}ee`, backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(234,230,220,0.08)",
      }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "transparent", border: "none", cursor: "pointer",
              color: "rgba(234,230,220,0.7)", fontSize: 14,
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            {designerPreview ? "Back to Course" : "My Courses"}
          </button>
          <h1 style={{
            color: CREAM, fontWeight: 700, fontSize: 17,
            textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap",
            flex: 1, textAlign: "center",
          }}>
            {tree?.title || "Course"}
          </h1>
          <div style={{ width: 120, display: "flex", justifyContent: "flex-end" }}>
            {designerPreview && (
              <Badge variant="secondary" className="gap-1 bg-amber-500/20 text-amber-200 border-amber-500/50">
                <Zap className="h-3 w-3" /> Designer
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* ── Dark hero section: banner + tree ── */}
      <section style={{ background: GREEN_DEEP, paddingBottom: 0 }}>

        {/* Course banner row — inside container for padding */}
        <div className="container mx-auto px-4 pt-6">
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 20 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <ProgressRing pct={completionPct} size={68} />
              <div style={{
                position: "absolute", inset: 0, display: "flex",
                alignItems: "center", justifyContent: "center",
                flexDirection: "column",
              }}>
                <span style={{ color: LIME, fontWeight: 800, fontSize: 15, lineHeight: 1 }}>{completionPct}%</span>
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ color: "rgba(234,230,220,0.5)", fontSize: 12, margin: "0 0 2px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Course Progress
              </p>
              <p style={{ color: CREAM, fontSize: 14, margin: 0 }}>
                <strong>{completedCount}</strong>
                <span style={{ color: "rgba(234,230,220,0.5)" }}> / {nodes.length} skills completed</span>
              </p>
              {tree?.target_certifications?.[0] && (
                <p style={{ color: "rgba(234,230,220,0.45)", fontSize: 12, margin: "3px 0 0" }}>
                  {tree.target_certifications[0].issuer} · {tree.target_certifications[0].name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Skill tree — full width, no container constraint */}
        {tree ? (
          <CourseTreeView
            nodes={nodes}
            prerequisites={prerequisites}
            selectedNodeId={selectedNodeId}
            onNodeSelect={n => setSelectedNodeId(n.id)}
            isDesignerPreview={designerPreview}
            nodeMastery={nodeMastery}
          />
        ) : (
          <p style={{ color: "rgba(234,230,220,0.4)", padding: "40px 0", textAlign: "center" }}>
            No skill tree yet.
          </p>
        )}

        {/* Node identity bar — back inside container */}
        <div className="container mx-auto px-4">
          <NodeIdentityBar
            node={selectedNode}
            onGoToNode={() => navigateToNode()}
            isDesignerPreview={designerPreview}
          />
        </div>
      </section>

      {/* ── Body ── */}
      <main style={{ background: "hsl(var(--background))" }}>
        <div className="container mx-auto px-4 py-8">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 32, alignItems: "start" }}>

            {/* Left column */}
            <div style={{ minWidth: 0 }}>

              {/* Modality tiles */}
              <div ref={modalityRef} style={{ marginBottom: 36 }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "hsl(var(--muted-foreground))", marginBottom: 6 }}>
                  Choose your mode
                </p>
                <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 18, color: "hsl(var(--foreground))", lineHeight: 1.3 }}>
                  How would you like to study{" "}
                  <span style={{
                    display: "inline-block",
                    background: GREEN_DEEP,
                    color: CREAM,
                    borderRadius: 8,
                    padding: "2px 12px",
                    fontSize: 22,
                    fontWeight: 700,
                  }}>
                    {selectedNode?.title ?? "…"}
                  </span>
                  ?
                </h2>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: 10,
                }}>
                  <ModalityTile
                    icon={<BookOpen style={{ width: 24, height: 24 }} />}
                    label="Read"
                    sublabel="Text + media"
                    onClick={() => handleModeClick("read")}
                    disabled={!selectedNode || (selectedNode.user_status === "locked" && !designerPreview)}
                  />
                  <ModalityTile
                    icon={<Bot style={{ width: 24, height: 24 }} />}
                    label="AI Tutor"
                    sublabel="Chat mode"
                    onClick={() => handleModeClick("tutor")}
                    disabled={!selectedNode || (selectedNode.user_status === "locked" && !designerPreview)}
                  />
                  <ModalityTile
                    icon={<Mic style={{ width: 24, height: 24 }} />}
                    label="Voice"
                    sublabel="Talk it out"
                    onClick={() => handleModeClick("voice")}
                    disabled={!selectedNode || (selectedNode.user_status === "locked" && !designerPreview)}
                  />
                  <ModalityTile
                    icon={<Layers style={{ width: 24, height: 24 }} />}
                    label="Flashcards"
                    sublabel="Quick review"
                    onClick={() => handleModeClick("flash")}
                    disabled={!selectedNode || (selectedNode.user_status === "locked" && !designerPreview)}
                  />
                  <ModalityTile
                    icon={<MonitorPlay style={{ width: 24, height: 24 }} />}
                    label="Slides"
                    sublabel="Presentation"
                    onClick={() => handleModeClick("slides")}
                    disabled={!selectedNode || (selectedNode.user_status === "locked" && !designerPreview)}
                  />
                  <ModalityTile
                    icon={<ClipboardCheck style={{ width: 24, height: 24 }} />}
                    label="Exam"
                    sublabel="Test yourself"
                    onClick={() => handleModeClick("exam")}
                    disabled={!selectedNode || (selectedNode.user_status === "locked" && !designerPreview)}
                  />
                </div>
              </div>

              {/* Curriculum accordion */}
              <div>
                <h2 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, color: "hsl(var(--foreground))" }}>
                  Curriculum
                </h2>
                {curriculumGroups.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No nodes yet.</p>
                ) : (
                  <Accordion type="multiple" defaultValue={curriculumGroups.map((_, i) => `group-${i}`)}>
                    {curriculumGroups.map((group, gi) => (
                      <AccordionItem key={gi} value={`group-${gi}`}>
                        <AccordionTrigger className="font-semibold text-sm">
                          <span className="flex items-center gap-2">
                            {group.label}
                            <span className="text-muted-foreground font-normal text-xs">
                              ({group.nodes.filter(n => n.user_status === "completed").length}/{group.nodes.length})
                            </span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          {group.description && (
                            <p className="text-xs text-muted-foreground mb-3 px-1 leading-relaxed">
                              {group.description}
                            </p>
                          )}
                          <div className="space-y-1 pt-1">
                            {group.nodes.map(node => (
                              <button
                                key={node.id}
                                onClick={() => {
                                  setSelectedNodeId(node.id);
                                  if (node.user_status !== "locked" || designerPreview) {
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                  }
                                }}
                                disabled={node.user_status === "locked" && !designerPreview}
                                className="w-full text-left rounded-lg px-3 py-2 flex items-center gap-3 transition-colors"
                                style={{
                                  background: selectedNodeId === node.id ? "rgba(47,97,71,0.12)" : "transparent",
                                  border: selectedNodeId === node.id ? `1px solid rgba(47,97,71,0.3)` : "1px solid transparent",
                                  opacity: node.user_status === "locked" && !designerPreview ? 0.45 : 1,
                                  cursor: node.user_status === "locked" && !designerPreview ? "not-allowed" : "pointer",
                                }}
                              >
                                {node.user_status === "completed" && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />}
                                {node.user_status === "in_progress" && <Target className="h-4 w-4 text-primary shrink-0" />}
                                {node.user_status === "available"   && <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                                {node.user_status === "locked"      && <Lock className="h-4 w-4 text-muted-foreground shrink-0" />}
                                <span className="flex-1 text-sm font-medium truncate">{node.title}</span>
                                {node.is_key_node && (
                                  <Star className="h-3 w-3 shrink-0" style={{ color: LIME, fill: LIME }} />
                                )}
                                {selectedNodeId === node.id && (
                                  <ChevronRight className="h-4 w-4 shrink-0 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                          {group.id && (
                            <FurtherReading nodeId={group.id} nodeName={group.label} courseId={id} />
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            </div>

            {/* Right sidebar */}
            <aside style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* AI Coach Suggestions — top of sidebar */}
              {!designerPreview && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bot className="h-4 w-4" />
                      AI Coach Suggestions
                    </CardTitle>
                    <CardDescription>Personalized suggestions based on your progress</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {coachSuggestions.length > 0 ? (
                      coachSuggestions.slice(0, 2).map((s) => (
                        <CoachSuggestionCard
                          key={s.id}
                          suggestion={s}
                          onDismiss={async (dismissedId) => {
                            await supabase
                              .from("coach_suggestions")
                              .update({ is_dismissed: true, dismissed_at: new Date().toISOString() })
                              .eq("id", dismissedId);
                            setCoachSuggestions((prev) => prev.filter((x) => x.id !== dismissedId));
                          }}
                          onAct={async (actedId) => {
                            await supabase
                              .from("coach_suggestions")
                              .update({ is_acted_upon: true, acted_upon_at: new Date().toISOString() })
                              .eq("id", actedId);
                            setCoachSuggestions((prev) => prev.filter((x) => x.id !== actedId));
                          }}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No suggestions yet. Keep learning and check back!</p>
                    )}
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setCoachPanelOpen(true)}
                    >
                      <Bot className="h-4 w-4" />
                      Chat with Coach
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Coach Panel (slide-out drawer) */}
              {!designerPreview && id && (
                <CoachPanel
                  open={coachPanelOpen}
                  onOpenChange={setCoachPanelOpen}
                  courseId={id}
                  courseTitle={tree?.title ?? "Course"}
                  onSuggestionsRefetch={async () => {
                    const { data } = await supabase
                      .from("coach_suggestions")
                      .select("id, message, suggestion_type, cta_label, cta_url, priority, node_id, course_id")
                      .eq("user_id", session!.user.id)
                      .eq("course_id", id!)
                      .eq("is_dismissed", false)
                      .order("priority", { ascending: false })
                      .order("generated_at", { ascending: false })
                      .limit(2);
                    setCoachSuggestions(data || []);
                  }}
                />
              )}

              {/* Objectives */}
              {objectives.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Learning Objectives</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="space-y-3">
                      {objectives.map((obj) => {
                        const om = objectiveMastery[obj.id];
                        const pct = om ? Math.round(om.mastery_score * 100) : 0;
                        return (
                          <li key={obj.id} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium leading-snug">{obj.title}</p>
                              {obj.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">{obj.description}</p>
                              )}
                              {pct > 0 && (
                                <div className="mt-2">
                                  <div className="w-full bg-muted rounded-full h-1.5">
                                    <div
                                      className="h-1.5 rounded-full transition-all"
                                      style={{ width: `${pct}%`, background: LIME }}
                                    />
                                  </div>
                                  <span className="text-xs text-muted-foreground">{pct}% mastery</span>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Certifications */}
              {tree?.target_certifications && tree.target_certifications.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Target Certifications</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {tree.target_certifications.map((cert, i) => (
                      <div key={i}>
                        <p className="text-sm font-semibold">{cert.name}</p>
                        <p className="text-xs text-muted-foreground">{cert.issuer}</p>
                        {cert.domains && cert.domains.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {cert.domains.map((d, j) => (
                              <Badge key={j} variant="secondary" className="text-xs">{d}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}


              {/* Your Progress */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Your Progress</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Completed</span>
                    <span className="font-semibold">{completedCount} / {nodes.length}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${completionPct}%`, background: "hsl(var(--primary))" }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="text-center p-2 rounded-lg bg-muted/50">
                      <p className="text-lg font-bold">{nodes.filter(n => n.user_status === "in_progress").length}</p>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-muted/50">
                      <p className="text-lg font-bold">{nodes.filter(n => n.is_key_node && n.user_status === "completed").length}</p>
                      <p className="text-xs text-muted-foreground">Key Skills</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LearnCourse;
